import React, { useState, useRef, useEffect } from "react";
import { X, UserPlus, UserMinus } from "lucide-react";
import { useUser } from "../contexts/UserContext";
import { useProject } from "../contexts/ProjectContext";
import { useNotification } from "../contexts/NotificationContext";
import Avatar from "./Avatar";

const AssignUserModal = ({
  project,
  card,
  onClose,
  onUserAssigned,
  onProjectUpdated,
}) => {
  const { users } = useUser();
  const { addProjectMember, removeProjectMember, fetchProject } = useProject();
  const { showToast } = useNotification();
  const [loading, setLoading] = useState({});
  const [localProject, setLocalProject] = useState(project);
  const modalRef = useRef(null);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const handleAssignUser = async (user) => {
    setLoading((prev) => ({ ...prev, [user._id]: true }));
    try {
      if (card) {
        // Assign to card
        const { cardAPI } = await import("../utils/api");
        const response = await cardAPI.assignUser(card._id, user._id);

        if (response.data.success) {
          showToast("User assigned to card successfully!", "success");
          if (onUserAssigned) {
            onUserAssigned();
          }
        }
      } else {
        // Assign to project
        const response = await addProjectMember(
          project._id,
          user.email,
          "member"
        );
        showToast(
          response.message || "User added to project successfully!",
          "success"
        );

        // Update local project state
        console.log("📝 AssignUser response:", response);
        if (response.project) {
          setLocalProject(response.project);
          console.log("✅ Local project state updated");
        } else {
          console.log("⚠️ No project data in response");
        }

        // Notify parent component to refresh
        if (onProjectUpdated) {
          onProjectUpdated();
        }
      }
    } catch (error) {
      console.error("Error assigning user:", error);
      showToast("Failed to assign user", "error");
    } finally {
      setLoading((prev) => ({ ...prev, [user._id]: false }));
    }
  };

  const handleRemoveUser = async (user) => {
    setLoading((prev) => ({ ...prev, [user._id]: true }));
    try {
      if (card) {
        // Remove from card
        const { cardAPI } = await import("../utils/api");
        const response = await cardAPI.unassignUser(card._id, user._id);

        if (response.data.success) {
          showToast("User removed from card successfully!", "success");
          if (onUserAssigned) {
            onUserAssigned();
          }
        }
      } else {
        // Remove from project
        const response = await removeProjectMember(project._id, user._id);
        showToast(
          response.message || "User removed from project successfully!",
          "success"
        );

        // Update local project state
        console.log("📝 RemoveUser response:", response);
        if (response.project) {
          setLocalProject(response.project);
          console.log("✅ Local project state updated");
        } else {
          console.log("⚠️ No project data in response");
        }

        // Notify parent component to refresh
        if (onProjectUpdated) {
          onProjectUpdated();
        }
      }
    } catch (error) {
      console.error("Error removing user:", error);
      showToast("Failed to remove user", "error");
    } finally {
      setLoading((prev) => ({ ...prev, [user._id]: false }));
    }
  };

  const isAssigned = (user) => {
    if (card) {
      return card.assignees?.some((assignee) => {
        if (typeof assignee === "object") {
          return assignee._id === user._id || assignee.id === user._id;
        }
        return assignee === user._id;
      });
    }
    // Use localProject state for real-time updates
    const currentProject = localProject || project;
    return currentProject.members?.some((member) => {
      if (typeof member === "object") {
        return member?.user?._id === user?._id || member?.user === user?._id;
      }
      return member === user?._id;
    });
  };
  console.log({ project, users });
  return (
    <div className="modal-overlay">
      <div ref={modalRef} className="modal-content max-w-2xl rounded-lg">
        <div className="bg-[#f1f2f4] dark:bg-slate-900 flex items-center justify-between px-4 h-[72px] min-h-[72px] border-b border-secondary-200 dark:border-slate-700 box-border">
          <div>
            <h2 className="text-xl font-bold text-[#222785] dark:text-slate-200 leading-tight">
              {card ? "Assign Users to Card" : "Manage Project Members"}
            </h2>
            <p className="text-[#131313] text-xs dark:text-slate-400 leading-tight">
              {card ? card.title : (localProject || project).name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center rounded-lg hover:bg-gray-400 hover:bg-opacity-20 transition-colors text-gray-600 dark:text-slate-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4">
          <div className="space-y-4">
            <h3 className="text-base font-semibold">
              {card ? "Project Members" : "All Users"}
            </h3>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {(card
                ? project?.members?.filter((user) => {
                    if (typeof user === "object") {
                      return user?.user;
                      // return (
                      //   member.user?._id === user._id ||
                      //   member.user === user._id
                      // );
                    }
                    // return member === user._id;
                  })
                : users
              ).map((projectUser) => {
                const user = card ? projectUser?.user : projectUser;
                const role = card ? projectUser?.role : projectUser?.role;

                const isAssignedUser = isAssigned(user);
                const isLoading = loading[user?._id];

                return (
                  <div
                    key={user?._id}
                    className="flex items-center justify-between px-3 h-[72px] min-h-[72px] max-h-[72px] bg-secondary-50 rounded-lg mx-2 my-2 dark:bg-slate-800 dark:text-slate-300 dark:border dark:border-slate-700 box-border"
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1 mr-3">
                      <Avatar user={user} size="sm" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-xs text-secondary-900 dark:text-slate-200 truncate">
                            {user?.name || user?.email || "Unknown User"}
                          </p>
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                              role === "admin"
                                ? "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300"
                                : "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                            }`}
                          >
                            {role || "member"}
                          </span>
                        </div>
                        <p className="text-xs text-secondary-500 dark:text-slate-400 truncate mt-0.5">
                          {user?.email}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {isAssignedUser ? (
                        <button
                          onClick={() => handleRemoveUser(user)}
                          disabled={isLoading}
                          className="h-8 min-h-[32px] flex items-center space-x-1.5 px-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-200 disabled:opacity-50 dark:bg-red-900/30 dark:text-red-300 text-xs font-medium"
                        >
                          <UserMinus className="w-3.5 h-3.5" />
                          <span>
                            {isLoading ? "Removing..." : "Remove"}
                          </span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAssignUser(user)}
                          disabled={isLoading}
                          className="h-8 min-h-[32px] flex items-center space-x-1.5 px-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200 disabled:opacity-50 dark:bg-blue-900/30 dark:text-blue-300 text-xs font-medium"
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          <span>
                            {isLoading ? "Adding..." : "Add"}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-end pt-4 border-t border-secondary-200 dark:border-slate-700 mt-4">
            <button
              onClick={onClose}
              className="h-8 min-h-[32px] px-4 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 text-xs font-medium transition-colors flex items-center justify-center"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignUserModal;
